import axios from 'axios';

// Create axios instance with default config
// Use empty baseURL so requests go through CRA proxy (package.json "proxy" field)
// In production, set REACT_APP_API_BASE_URL to the actual API domain
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('pr_token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    // Clear token on 401 (invalid/expired)
    if (error.response?.status === 401) {
      const isAuthEndpoint = error.config?.url?.includes('/auth/');
      if (!isAuthEndpoint) {
        localStorage.removeItem('pr_token');
      }
    }

    if (error.response) {
      console.error('API Error:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Request setup error:', error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Build a clean params object, stripping empty/null/undefined values.
 */
function cleanParams(params) {
  const cleaned = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

// API methods
export const api = {
  // ─── Data Endpoints ──────────────────────
  offenders: {
    search: (options = {}, config = {}) => {
      if (typeof options === 'string') {
        return apiClient.get('/api/data/search/', { params: { q: options }, ...config });
      }
      const params = cleanParams(options);
      return apiClient.get('/api/data/search/', { params, ...config });
    },

    searchByZipCode: (zipCode, config = {}) =>
      apiClient.get('/api/data/search/', { params: { zipCode }, ...config }),

    getById: (id, config = {}) =>
      apiClient.get(`/api/data/offenders/${id}/`, config),

    nearby: (latitude, longitude, radius = 5, options = {}, config = {}) =>
      apiClient.get('/api/data/offenders/nearby/', {
        params: cleanParams({ latitude, longitude, radius, ...options }),
        ...config,
      }),
  },

  stats: {
    get: (options = {}) => {
      const params = cleanParams(options);
      return apiClient.get('/api/data/stats/', { params });
    },
  },

  // ─── Auth Endpoints ──────────────────────
  auth: {
    register: (data) => apiClient.post('/api/billing/auth/register/', data),
    login: (data) => apiClient.post('/api/billing/auth/login/', data),
    logout: () => apiClient.post('/api/billing/auth/logout/'),
    getUser: () => apiClient.get('/api/billing/auth/user/'),
    updateUser: (data) => apiClient.put('/api/billing/auth/user/', data),
    resetPassword: (email) => apiClient.post('/api/billing/auth/password/reset/', { email }),
    resetPasswordConfirm: (data) => apiClient.post('/api/billing/auth/password/reset/confirm/', data),
  },

  // ─── Billing Endpoints ──────────────────────
  billing: {
    // API Keys
    getKeys: () => apiClient.get('/api/billing/keys/'),
    createKey: (name = 'Default') => apiClient.post('/api/billing/keys/', { name }),
    revokeKey: (id) => apiClient.delete(`/api/billing/keys/${id}/`),

    // Usage
    getUsage: () => apiClient.get('/api/billing/usage/'),
    getUsageHistory: () => apiClient.get('/api/billing/usage/history/'),
    getRecentCalls: () => apiClient.get('/api/billing/usage/calls/'),

    // Payment
    getPaymentMethod: () => apiClient.get('/api/billing/payment-method/'),
    addPaymentMethod: (paymentMethodId) =>
      apiClient.post('/api/billing/payment-method/', { payment_method_id: paymentMethodId }),
    removePaymentMethod: () => apiClient.delete('/api/billing/payment-method/'),
    createSetupIntent: () => apiClient.post('/api/billing/setup-intent/'),

    // Invoices
    getInvoices: () => apiClient.get('/api/billing/invoices/'),
  },
};

export default api;
